/**
 * iter 42 — Mobile driver app shell.
 *
 * Provides:
 *  - Viewport-locked layout (top bar + scrollable body + bottom nav)
 *  - Re-applies the user's dark/light theme on mount (same prefer flag the
 *    web app reads in `lib/auth.jsx`)
 *
 * Capacitor migration note: the entire <MobileShell> is designed to run
 * unchanged inside a Capacitor (iOS / Android) WebView — env(safe-area-inset-*)
 * is already wired in `mobile.css` and all native-only APIs (camera, GPS) are
 * called through the standard web APIs which Capacitor polyfills.
 */
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Briefcase, MapPin, Scan } from "lucide-react";
import { useAuth } from "../lib/auth";
import "./mobile.css";

const NAV = [
  { key: "account", to: "/m/account", icon: User, label: "Account" },
  { key: "jobs", to: "/m/jobs", icon: Briefcase, label: "Jobs" },
  { key: "routes", to: "/m/routes", icon: MapPin, label: "Routes" },
  { key: "scan", to: "/m/scan", icon: Scan, label: "QR Scan" },
];

export default function MobileShell({ title, back, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Re-apply theme on every mount in case the user toggled it on this device.
  useEffect(() => {
    const html = document.documentElement;
    if (user?.theme === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
  }, [user?.theme]);

  // Lock the meta-viewport so PWA / Capacitor renders 1:1 on mobile.
  useEffect(() => {
    let tag = document.querySelector('meta[name=viewport]');
    const prev = tag?.getAttribute("content");
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "viewport";
      document.head.appendChild(tag);
    }
    tag.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no",
    );
    return () => { if (prev) tag.setAttribute("content", prev); };
  }, []);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  // iter 43 Phase 2 — notary commission-expiry warning.
  // Surfaces when the user's commission expires in less than 30 days. Reuses
  // the existing `users.notary_expiration_date` column (`/auth/me` already
  // returns it) — we did NOT introduce a parallel `notary_commission_expiry`
  // field for the banner.
  const daysToExpiry = (() => {
    if (!user?.is_notary || !user?.notary_expiration_date) return null;
    const exp = new Date(user.notary_expiration_date + "T00:00:00Z");
    const ms = exp.getTime() - Date.now();
    return Math.floor(ms / 86400000);
  })();
  const notaryExpiringSoon = daysToExpiry !== null && daysToExpiry <= 30;

  return (
    <div className="mobile-shell" data-testid="mobile-shell">
      <div className="m-top" data-testid="mobile-topbar">
        {back && (
          <button
            type="button"
            className="m-link"
            onClick={() => navigate(back === true ? -1 : back)}
            data-testid="mobile-topbar-back"
            aria-label="Back"
          >&larr;</button>
        )}
        <div className="m-top-title" data-testid="mobile-topbar-title">
          {title || user?.org_name || "LogBook Legal"}
        </div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
          {user?.name || user?.email}
        </div>
      </div>
      {notaryExpiringSoon && (
        <div
          className="m-card"
          style={{
            margin: "8px 12px 0",
            background: daysToExpiry < 0 ? "#fee2e2" : "#fef3c7",
            color: daysToExpiry < 0 ? "#991b1b" : "#92400e",
            border: "none",
          }}
          data-testid="m-notary-expiry-banner"
        >
          <strong>
            {daysToExpiry < 0
              ? `Notary commission EXPIRED ${Math.abs(daysToExpiry)} day${Math.abs(daysToExpiry) === 1 ? "" : "s"} ago.`
              : `Notary commission expires in ${daysToExpiry} day${daysToExpiry === 1 ? "" : "s"}.`}
          </strong>
          <div className="m-card-sub" style={{ color: "inherit" }}>
            Update your commission number / expiry on the Account page.
          </div>
        </div>
      )}
      <div className="m-body" data-testid="mobile-body">
        {children}
      </div>
      <nav className="m-bot" data-testid="mobile-bottom-nav" aria-label="Primary">
        {NAV.map(({ key, to, icon: Icon, label }) => (
          <button
            key={key}
            type="button"
            className={`m-bot-btn ${isActive(to) ? "active" : ""}`}
            onClick={() => navigate(to)}
            data-testid={`mobile-nav-${key}`}
            aria-current={isActive(to) ? "page" : undefined}
          >
            <Icon size={20} strokeWidth={1.75} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
